import * as React from 'react';
import { isEmpty } from 'ramda';
import { debounce, getEventValue } from '@cian/newbuilding-utils';
import { Input, InputAdornment, Outside } from '@cian/ui-kit';
import { IconActionClose16, IconActionSearch16 } from '@cian/ui-kit-design-tokens/icons';

import { DEFAULT_INPUT_TIMEOUT } from '../../constants/timeouts';
import { ISuggestionGroup, TSuggestionSelectedItem } from '../../types/suggestions';
import { inRangeMemo } from '../../utils/functional';

import { SuggestCategory } from './components/SuggestCategory';
import { EMPTY_SUGGESTIONS, SUGGEST_CATEGORIES } from './constants';
import { requestGeocodedSearchData, requestSuggestions } from './helpers';
import { ISuggestData, TBoundedBy } from './types';
import * as styles from './Suggest.css';

export interface ISuggestProps {
  ariaLabel?: string;
  boundedBy?: TBoundedBy;
  locationId: number;
  onSuggestionSelect(suggestion: TSuggestionSelectedItem): void;
  placeholder?: string;
}

export const Suggest: React.FC<ISuggestProps> = ({
  ariaLabel,
  boundedBy,
  locationId,
  onSuggestionSelect,
  placeholder,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [pure, setPure] = React.useState(true);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [suggestions, setSuggestions] = React.useState<ISuggestData>(EMPTY_SUGGESTIONS);
  const [term, setTerm] = React.useState('');

  const allSuggests = React.useMemo(() => {
    return SUGGEST_CATEGORIES.reduce<ISuggestionGroup[]>((acc, category) => acc.concat(suggestions[category.key]), []);
  }, [suggestions]);

  const fetchSuggestions = React.useCallback(
    async (searchTerm: string) => {
      setLoading(true);

      try {
        const suggestionsData = await requestSuggestions({ boundedBy, locationId, term: searchTerm });
        setSuggestions(suggestionsData);
      } catch {
        // ignore error
      } finally {
        setLoading(false);
      }
    },
    [boundedBy, locationId],
  );

  const debounceFetchSuggestions = React.useRef(debounce(DEFAULT_INPUT_TIMEOUT, fetchSuggestions));

  React.useEffect(() => {
    debounceFetchSuggestions.current = debounce(DEFAULT_INPUT_TIMEOUT, fetchSuggestions);
  }, [fetchSuggestions]);

  const getSuggestions = React.useCallback((searchTerm: string) => {
    const preparedTerm = searchTerm.trim();

    /* istanbul ignore else */
    if (preparedTerm) {
      debounceFetchSuggestions.current(preparedTerm);
    }
  }, []);

  const clearState = React.useCallback(() => {
    setSuggestions(EMPTY_SUGGESTIONS);
    setSelectedIndex(0);
  }, []);

  const handleTermChange = React.useCallback(
    (newTerm: string) => {
      if (newTerm) {
        getSuggestions(newTerm);
      } else {
        clearState();
      }

      setTerm(newTerm);
    },
    [getSuggestions, clearState],
  );

  const selectGeoSuggestion = React.useCallback(
    async (suggestion: ISuggestionGroup) => {
      setLoading(true);

      try {
        const searchData = await requestGeocodedSearchData(suggestion);

        /* istanbul ignore else */
        if (searchData) {
          onSuggestionSelect(searchData);
        }
      } catch {
        // ignore error
      } finally {
        setLoading(false);
      }
    },
    [onSuggestionSelect],
  );

  const selectSimpleSuggestion = React.useCallback(
    (suggestion: ISuggestionGroup) => {
      onSuggestionSelect({
        data: suggestion,
        dataType: 'simple',
        type: suggestion.type,
      });
    },
    [onSuggestionSelect],
  );

  const handleSelect = React.useCallback(
    (suggestion: ISuggestionGroup) => {
      setTerm('');
      setPure(true);

      if (suggestion.type === 'geo') {
        selectGeoSuggestion(suggestion);
      } else {
        selectSimpleSuggestion(suggestion);
      }
    },
    [selectGeoSuggestion, selectSimpleSuggestion],
  );

  const handleHover = React.useCallback(
    (suggest: ISuggestionGroup) => {
      const index = allSuggests.findIndex(d => d.value === suggest.value && d.type === suggest.type);
      setSelectedIndex(index);
    },
    [allSuggests],
  );

  const toggleSuggestBlur = React.useCallback(() => {
    setPure(true);
  }, []);

  const toggleSuggestFocus = React.useCallback(() => {
    setPure(false);
  }, []);

  const handleClose = React.useCallback(() => {
    setTerm('');
    setPure(true);
  }, []);

  const addSelectedIndex = React.useCallback(
    (i: number) => {
      const range = inRangeMemo(0, allSuggests.length - 1);
      setSelectedIndex(prevIndex => range(prevIndex + i));
    },
    [allSuggests.length],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      const selectedSuggest = allSuggests[selectedIndex];

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          addSelectedIndex(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          addSelectedIndex(-1);
          break;
        case 'Tab':
        case 'Enter':
          handleSelect(selectedSuggest);
          break;
        case 'Escape':
          toggleSuggestBlur();
          break;
        default:
          break;
      }
    },
    [allSuggests, selectedIndex, addSelectedIndex, handleSelect, toggleSuggestBlur],
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent) => {
      const value = getEventValue(event);
      handleTermChange(value);
      setPure(false);
    },
    [handleTermChange],
  );

  const getInputProps = React.useCallback(() => {
    return {
      ['aria-label']: ariaLabel,
      loading,
      onChange: handleChange,
      onFocus: toggleSuggestFocus,
      onKeyDown: handleKeyDown,
      placeholder,
      size: 'M' as const,
      value: term,
    };
  }, [ariaLabel, loading, handleChange, toggleSuggestFocus, handleKeyDown, placeholder, term]);

  const selectedSuggest = allSuggests[selectedIndex];

  return (
    <Outside onOutside={toggleSuggestBlur}>
      <div className={styles['container']}>
        <Input
          {...getInputProps()}
          leftAdornment={
            <InputAdornment>
              <IconActionSearch16 color="icon-main-default" />
            </InputAdornment>
          }
          data-testid="SuggestInput"
        />
        <div data-mark="Suggest" className={styles['suggest_list_handler']}>
          {!pure && (
            <div className={styles['suggest_list']}>
              <div className={styles['suggest_list_input_handler']}>
                <Input {...getInputProps()} autoFocus />

                <span onClick={handleClose} className={styles['suggest_list_close_icon']} aria-label="Закрыть">
                  <IconActionClose16 color="current_color" />
                </span>
              </div>

              {SUGGEST_CATEGORIES.map(category => {
                const group = suggestions[category.key];
                const firstInGroup = group[0];

                const selectedValue =
                  selectedSuggest && firstInGroup && selectedSuggest.type === firstInGroup.type
                    ? selectedSuggest.value
                    : undefined;

                return (
                  !isEmpty(group) && (
                    <SuggestCategory
                      key={category.key}
                      label={category.label}
                      categoryType={category.key}
                      suggests={group}
                      icon={category.icon}
                      selected={selectedValue}
                      onHover={handleHover}
                      onSelect={handleSelect}
                      highlight={term}
                    />
                  )
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Outside>
  );
};
